<template>
    <KForm
        ref="formRef"
        id="preanestesibedomning"
        :config-module-name="ConfigModuleName.Operation"
        :config-sub-module-name="OperationConfigModuleName.Preanestesibedomning"
        :use-formulate="true"
        :initial-values-getter="initForm"
        cancel-content="Avbryt"
        @cancel="cancelForm"
        :triggers="formTriggers"
        :converters="formConverters"
        :listeners="formListeners()"
        :form-errors="formErrors"
        :errors="inputErrors"
    >
        <template #header>
            <h2>{{ heading }}</h2>
            <sign-frame
                v-if="signVisible"
                title="preanestesibedömning"
                :url="signUrl"
                :data="signData"
                :tabbnamn="tabbnamn"
                @success="handleSignSuccess"
                @error="handleSignError"
                @cancel="handleSignCancel"
            />
            <AnmalanInfoBar
                v-if="isNonEmptyObject(currentOperationsanmalan)"
                :anmalan="currentOperationsanmalan"
                :showPreanestesibedömningStatus="true"
                :showUpdated="false"
            />
        </template>
        <template #default>
            <KSection name="operation" heading="Operation" class="operation-section" number="1">
                <Operationsoverblick
                    v-if="isNonEmptyObject(currentOperationsanmalan)"
                    :showEmptyValues="true"
                    :anmalan="currentOperationsanmalan"
                    @openAnmalan="openOperationsinformation"
                />
            </KSection>
            <KSection name="patientdata" heading="Patientdata" :collapsible="true" :numbered="true" :number="2">
                <div class="form-content">
                    <div class="form-section span-all">
                        <KCheckbox name="neurologisk_sjukdom" label="Neurologisk sjukdom" />
                        <KText
                            type="textarea"
                            name="neurologisk_sjukdom_text"
                            label="Neurologisk sjukdom beskrivning"
                            class="span-two"
                        />
                    </div>
                    <div class="form-section span-all">
                        <KCheckbox name="hjart_karlsjukdom" label="Hjärt-kärlsjukdom" />
                        <KText
                            type="textarea"
                            name="hjart_karlsjukdom_text"
                            label="Hjärt-kärlsjukdom beskrivning"
                            class="span-two"
                        />
                    </div>

                    <div class="form-section span-all">
                        <KCheckbox name="lungsjukdom" label="Lungsjukdom" />
                        <KText
                            type="textarea"
                            name="lungsjukdom_text"
                            label="Lungsjukdom beskrivning"
                            class="span-two"
                        />
                    </div>

                    <div class="form-section span-all">
                        <KCheckbox name="njursjukdom" label="Njursjukdom" />
                        <KText
                            type="textarea"
                            name="njursjukdom_beskrivning"
                            label="Njursjukdom beskrivning"
                            class="span-two"
                        />
                    </div>

                    <div class="form-section span-all">
                        <KCheckbox name="blodningsbenagenhet" label="Blödning/Trombosbenägenhet" />
                        <KText
                            type="textarea"
                            name="blodningsbenagenhet_beskrivning"
                            label="Blödning/Trombosbenägenhet beskrivning"
                            class="span-two"
                        />
                    </div>

                    <div class="form-section span-all">
                        <KCheckbox name="antikoagulantia" label="Antikoagulantia" />
                        <KText
                            type="textarea"
                            name="antikoagulantia_beskrivning"
                            label="Antikoagulantia beskrivning"
                            class="span-two"
                        />
                    </div>

                    <div class="form-section span-all">
                        <KCheckbox name="overkanslighet" label="Överkänslighet" />
                        <KText
                            type="textarea"
                            name="overkanslighet_text"
                            label="Överkänslighet beskrivning"
                            class="span-two"
                        />
                    </div>

                    <div class="form-section span-all">
                        <KAutocomplete name="diabetes" label="Diabetes" :options="jaNej" />

                        <DiabetesBehandlingsformAutocomplete />
                    </div>

                    <div class="form-section span-all">
                        <KAutocomplete name="rokare" label="Rökare" :options="jaNejOkant" />

                        <KAutocomplete name="snusare" label="Snusare" :options="jaNejOkant" />
                    </div>

                    <div class="form-section span-all">
                        <KText name="anamnes" label="Annan sjukdom, anamnes" type="textarea" class="span-two" />

                        <KText name="kommentar_anestesi" label="Tidigare anestesi" type="textarea" class="span-two" />

                        <KText type="textarea" name="lakemedelsallergi" label="Läkemedelsallergi" class="span-two" />

                        <KText type="textarea" name="aktuella_lakemedel" label="Aktuella läkemedel" class="span-two" />

                        <KNumber name="puls" label="Puls" :min="0" :max="350" />

                        <KGroup name="blodtryck" class="span-two">
                            <KNumber name="blodtryck_systoliskt" label="Blodtryck syst" :min="0" :max="999" />

                            <KNumber name="blodtryck_diastoliskt" label="Blodtryck diast" :min="0" :max="999" />
                        </KGroup>
                    </div>

                    <div class="form-section span-all">
                        <KNumber name="langd" label="Längd (cm)" :minValue="30" :maxValue="240" />

                        <KNumber name="vikt" label="Vikt (kg)" :minValue="0" :maxValue="350" validation="decimals:1" />

                        <BMIDisplay ref="bmiDisplay" />
                    </div>

                    <div class="form-section span-all">
                        <KAutocomplete
                            name="lab"
                            label="Lab"
                            :options="[
                                { value: 'ua', label: 'Ua' },
                                { value: 'anmarkning', label: 'Anmärkning' },
                            ]"
                        />

                        <KText name="lab_anmarkning" label="Anmärkning lab" type="textarea" class="span-two" />
                    </div>

                    <div class="form-section span-all">
                        <KNumber name="senaste_hb" label="Senaste HB" :step="0.1" :min="0" :max="300" />

                        <KDateTimepicker name="senaste_hb_datum" label="Datum för HB" />
                    </div>

                    <div class="form-section span-all">
                        <KAutocomplete
                            name="ekg"
                            label="EKG"
                            :options="[
                                { value: 'ua', label: 'Ua' },
                                { value: 'anmarkning', label: 'Anmärkning' },
                            ]"
                        />

                        <KDateTimepicker name="ekg_datum" label="Datum för EKG" />

                        <KText name="ekg_anmarkning" label="Anmärkning EKG" type="textarea" class="span-two" />
                    </div>
                </div>
            </KSection>

            <KSection name="bedomning" heading="Bedömning" :collapsible="true" :numbered="true" :number="3">
                <div class="form-content">
                    <div class="form-section span-all">
                        <KAutocomplete name="asa" label="ASA" :options="ASA" />

                        <KText name="kommentar_asa" label="Kommentar ASA" type="textarea" class="span-two" />
                    </div>
                </div>
            </KSection>

            <KSection name="anestesi" heading="Anestesi" :collapsible="true" :numbered="true" :number="4">
                <div class="form-content">
                    <div class="form-section span-all">
                        <ProfileAutocomplete
                            name="anestesiolog"
                            label="Anestesiolog"
                            :urlParams="{
                                roll__code: 'anestesilakare',
                            }"
                        />

                        <HuvudanestesikodAutocomplete class="span-two" />
                    </div>

                    <BianestesikodRepeater />

                    <div class="form-section span-all">
                        <AnestesiAutocomplete />

                        <BlockadAutocomplete />

                        <PremedicineringRepeater />

                        <KText
                            name="information_till_koordinator"
                            label="Information till koordinator"
                            type="textarea"
                            class="span-two"
                        />
                        <KText
                            name="ordinationer_operation"
                            label="Information till operation/anestesi"
                            type="textarea"
                            class="span-two"
                        />

                        <KText
                            name="ordinationer_avdelning"
                            label="Information till vårdavdelning"
                            type="textarea"
                            class="span-two"
                        />

                        <KText
                            name="lakemedel_morgon"
                            label="Ordinarie läkemedel som SKA ges på operationsdagens morgon"
                            type="textarea"
                            class="span-two"
                        />
                    </div>
                </div>
            </KSection>

            <KSection name="godkannande" class="godkannande" heading="Godkännande inför operation">
                <div class="form-content">
                    <div class="form-section span-all">
                        <KAutocomplete
                            name="godkannande"
                            label="Godkännande"
                            :options="godkannanden"
                            validation="required"
                            validation-name="Godkännande"
                            :required="true"
                        />

                        <KDateTimepicker name="datum_godkannande" label="Datum för godkännande" />

                        <KDateTimepicker name="giltighetstid" label="Giltighetstid" />
                    </div>

                    <div class="form-section span-all">
                        <KText name="kommentar" label="Kommentar" type="textarea" class="span-two" />

                        <KDateTimepicker
                            name="komplettering_kravd_datum"
                            label="Startdatum komplettering krävd"
                            validation-name="Startdatum komplettering krävd"
                        />

                        <KCheckbox
                            name="komplettering_utford"
                            label="Komplettering utförd"
                            variant="complete"
                            disabled
                        />

                        <ProfileAutocomplete
                            name="accountableHealthcareProfessional"
                            label="Signeringsansvarig"
                            :urlParams="{
                                roll__code: 'anestesilakare',
                                signeringsansvarig: true,
                            }"
                            class="span-two"
                        />
                    </div>
                </div>
            </KSection>
        </template>
        <template #actions>
            <KButton type="button" size="primary" @click.prevent="saveForm" :disabled="!canEdit">
                Spara preanestesibedömning
            </KButton>
            <KButton type="button" size="primary" @click.prevent="signForm" :disabled="!canSign">
                <img src="@/assets/sign_white_shield.svg" alt="" />
                Spara och signera preanestesibedömning
            </KButton>
        </template>
    </KForm>
</template>

<script>
    import tabIcon from "@/assets/operationsanmalan.svg";
    import BaseTab from "@/tabs/mixins/BaseTab";
    import KCheckbox from "@/components/forms/inputs/Checkbox/KCheckbox.vue";
    import KText from "@/components/forms/inputs/Text/KText.vue";

    export default {
        extends: BaseTab,
        // eslint-disable-next-line vue/multi-word-component-names
        name: "Preanestesibedomning",
        tabName: "Preanestesibedömning",
        icon: tabIcon,
        patientBound: true,
    };
</script>

<script setup>
    import { ref, computed, defineProps, toRefs, nextTick, defineExpose } from "vue";
    import KForm from "@/components/forms/Form/KForm.vue";
    import KSection from "@/components/generic/KSection.vue";
    import { useStore } from "@/store";
    import emitter from "tiny-emitter/instance";
    import {
        CONFIG_PROVIDE_NAME,
        ConfigModuleName,
        OperationConfigModuleName,
    } from "@/store/modules/types/configModuleTypes";
    import { provide } from "vue";
    import KButton from "@/components/Button.vue";
    import Operationsinformation from "@/tabs/Operation/Operationsinformation.vue";
    import AnmalanInfoBar from "@/tabs/Operation/AnmalanInfoBar.vue";
    import Operationsoverblick from "@/tabs/Operation/Operationsoverblick.vue";
    import { flashMessage, openDialog } from "@/utils";
    import moment from "moment";
    import { cloneDeep } from "lodash";
    import { ASA, scrollToAndFocusError } from "./utils";
    import { isNonEmptyObject } from "@/tabs/Operation/utilsTs";
    import SignFrame from "@/components/Auth/SignFrame.vue";
    import PreanestesibedomningStatusGodkand from "@/assets/actionIcons/statuspreanestesibedomning/godkand.svg";
    import PreanestesibedomningStatusEjGodkand from "@/assets/actionIcons/statuspreanestesibedomning/ej_godkand.svg";
    import PreanestesibedomningStatusAterkallad from "@/assets/actionIcons/statuspreanestesibedomning/aterkallad.svg";
    import PreanestesibedomningStatusUtgangen from "@/assets/actionIcons/statuspreanestesibedomning/utgangen.svg";
    import PreanestesibedomningStatusAttGora from "@/assets/actionIcons/statuspreanestesibedomning/att_gora.svg";
    import PreanestesibedomningStatusKompletteringKravs from "@/assets/actionIcons/statuspreanestesibedomning/komplettering_kravs.svg";
    import PreanestesibedomningStatusKompletteringUtford from "@/assets/actionIcons/statuspreanestesibedomning/komplettering_utford.svg";
    import BianestesikodRepeater from "@/components/forms/reusables/BianestesikodRepeater.vue";
    import HuvudanestesikodAutocomplete from "@/components/forms/reusables/HuvudanestesikodAutocomplete.vue";
    import { convertAnestesikodData } from "@/store/modules/operation/anestesikodModule";
    import PremedicineringRepeater from "@/components/forms/reusables/PremedicineringRepeater.vue";
    import { convertPremedicineringData } from "@/store/modules/operation/premedicineringModule";
    import ProfileAutocomplete from "@/components/forms/reusables/ProfileAutocomplete.vue";
    import AnestesiAutocomplete from "@/components/forms/reusables/AnestesiAutocomplete.vue";
    import BlockadAutocomplete from "@/components/forms/reusables/BlockadAutocomplete.vue";
    import { convertAnestesiData } from "@/store/modules/operation/anestesiModule";
    import { convertBlockadData } from "@/store/modules/operation/blockadModule";
    import { getConfig, getErrorMessage, klinikenApi } from "@/api";
    import DiabetesBehandlingsformAutocomplete from "@/components/forms/reusables/DiabetesBehandlingsformAutocomplete.vue";
    import BMIDisplay from "@/components/forms/reusables/BMIDisplay.vue";
    import { convertDiagnoskodData } from "@/store/modules/diagnoskodModule";

    const store = useStore();

    const props = defineProps({
        tabId: Number,
        parameters: {},
    });

    provide(CONFIG_PROVIDE_NAME, {
        moduleName: ConfigModuleName.Operation,
        subModuleName: OperationConfigModuleName.Preanestesibedomning,
    });

    provide("showEmptyValues", false);

    const { tabId, parameters } = toRefs(props);

    const preanestesibedomningId = parameters?.value?.preanestesibedomningId ?? null;

    const currentOperationsanmalan = ref(null);
    const tabbnamn = "Preanestesibedömning";

    const formRef = ref();
    const formElement = computed(() => formRef.value?.$el);
    const formErrors = ref([]);
    const inputErrors = ref({});
    const bmiDisplay = ref();

    const signVisible = ref(false);
    const signUrl = ref("");
    const signData = ref({});
    const isSigned = ref(false);
    const godkannande = ref();
    const accountableHealthcareProfessional = ref();

    const heading = computed(() => {
        if (preanestesibedomningId) {
            return "Redigera Preanestesibedömning";
        } else {
            return "Skapa Preanestesibedömning";
        }
    });

    const userCanEdit = computed(() => {
        return can.value({ action: "update", subject: "preanestesibedomning" });
    });

    const userCanSign = computed(() => {
        return (
            userCanEdit.value &&
            currentProfile.value?.roll?.code === "anestesilakare" &&
            accountableHealthcareProfessional?.value === currentProfile.value?.pk
        );
    });

    const canEdit = computed(() => {
        if (!userCanEdit.value || isSigned.value || canSign.value) {
            return false;
        }
        return (
            !godkannande.value ||
            [paborjad, kompletteringKravs, kompletteringUtford, godkandPrimarsystem].includes(godkannande.value)
        );
    });

    const canSign = computed(() => {
        if (godkannande.value === godkandPrimarsystem) {
            return false;
        }
        if (!userCanSign.value) {
            return false;
        }

        return [godkand, ejGodkand].includes(godkannande.value) || isSigned.value;
    });

    const currentProfile = computed(() => store.getters["userData/currentProfile"]);
    const can = computed(() => store.getters["ability/can"]);
    const patient = computed(() => store.getters["patientData/patient"]);

    const jaNej = ref([
        { value: "JA", label: "Ja" },
        { value: "NEJ", label: "Nej" },
    ]);

    const jaNejOkant = [
        { value: "JA", label: "Ja" },
        { value: "NEJ", label: "Nej" },
        { value: "OKÄNT", label: "Okänt" },
    ];

    // FIXME Det här borde flyttas till utils.js där det redan finns en lista med statusar
    const attGora = "Att göra";
    const paborjad = "Påbörjad";
    const kompletteringKravs = "Komplettering krävs";
    const kompletteringUtford = "Komplettering utförd";
    const godkand = "Godkänd";
    const ejGodkand = "Ej godkänd";
    const aterkallad = "Återkallad";
    const utgangen = "Utgången";
    const godkandPrimarsystem = "Godkänd i primärsystem";
    const godkannanden = [
        { value: paborjad, disabled: false, label: paborjad },
        { value: kompletteringKravs, disabled: false, label: kompletteringKravs },
        {
            value: kompletteringUtford,
            disabled: false,
            label: kompletteringUtford,
        },
        { value: godkand, disabled: false, label: godkand },
        { value: godkandPrimarsystem, disabled: false, label: godkandPrimarsystem },
        { value: ejGodkand, disabled: false, label: ejGodkand },
        { value: aterkallad, disabled: true, label: aterkallad },
        { value: utgangen, disabled: true, label: utgangen },
    ];

    const getImgSrcPreanestesibedomning = (status) => {
        switch (status) {
            case attGora:
                return PreanestesibedomningStatusAttGora;
            case paborjad:
                return PreanestesibedomningStatusKompletteringKravs;
            case kompletteringKravs:
                return PreanestesibedomningStatusKompletteringKravs;
            case kompletteringUtford:
                return PreanestesibedomningStatusKompletteringUtford;
            case godkand:
                return PreanestesibedomningStatusGodkand;
            case ejGodkand:
                return PreanestesibedomningStatusEjGodkand;
            case aterkallad:
                return PreanestesibedomningStatusAterkallad;
            case utgangen:
                return PreanestesibedomningStatusUtgangen;
            case godkandPrimarsystem:
                return PreanestesibedomningStatusGodkand;
        }
    };

    const hasUnsavedData = computed(() => {
        return formRef.value?.hasUnsavedData();
    });

    defineExpose({
        hasUnsavedData,
    });

    /**
     * Bara längden trunkeras. Backend har den som PositiveIntegerField, medan vikten är DecimalField med en
     * decimal och ska visas som den är.
     */
    const getTruncatedValue = (value) => {
        return value ? Math.trunc(value) : null;
    };

    const getOperationsanmalan = async () => {
        try {
            const response = await klinikenApi(
                `/operation/anmalan/${parameters?.value?.operationsanmalanId}/`,
                getConfig()
            );
            return response.data;
        } catch (error) {
            openDialog(`Kunde inte hämta Operationsanmalan. ${getErrorMessage(error)}`, "warning");
            return {};
        }
    };

    const initForm = async () => {
        store.dispatch("tabs/displaySpinner", { id: tabId.value, display: true });

        currentOperationsanmalan.value = cloneDeep(await getOperationsanmalan());

        let formData = {};

        if (preanestesibedomningId) {
            formData = await loadPreanestesibedomning(preanestesibedomningId);

            if (!formData.accountableHealthcareProfessional && currentProfile.value?.roll?.code === "anestesilakare") {
                formData.accountableHealthcareProfessional = {
                    value: currentProfile.value.pk,
                    label: `${currentProfile.value.user_display.name}`,
                };
            }
        } else {
            formData = {
                diabetes: booleanToJaNej(currentOperationsanmalan.value?.diabetes),
                diabetes_lakemedelsform: currentOperationsanmalan.value?.diabetes_lakemedelsform,
                rokare: currentOperationsanmalan.value?.rokare,
                blodtryck_diastoliskt: currentOperationsanmalan.value?.blodtryck_diastoliskt,
                blodtryck_systoliskt: currentOperationsanmalan.value?.blodtryck_systoliskt,
                langd: getTruncatedValue(currentOperationsanmalan.value?.langd),
                vikt: currentOperationsanmalan.value?.vikt,

                anestesiolog:
                    currentProfile.value?.roll?.code === "anestesilakare"
                        ? {
                              value: currentProfile.value.pk,
                              label: `${currentProfile.value.user_display.name}`,
                          }
                        : null,
                accountableHealthcareProfessional:
                    currentProfile.value?.roll?.code === "anestesilakare"
                        ? {
                              value: currentProfile.value.pk,
                              label: `${currentProfile.value.user_display.name}`,
                          }
                        : null,
                huvudanestesikod: currentOperationsanmalan.value?.anestesi_display
                    ? convertAnestesikodData({
                          anestesikod: currentOperationsanmalan.value?.anestesi_display?.huvudanestesikod_display,
                      })
                    : null,
                bianestesikoder: currentOperationsanmalan.value?.anestesi_display
                    ? currentOperationsanmalan.value?.anestesi_display?.bianestesikoder_display?.map((item) => {
                          return {
                              id: convertAnestesikodData({ anestesikod: item }),
                          };
                      })
                    : [],
                anestesi: currentOperationsanmalan.value?.anestesi_display
                    ? convertAnestesiData({ anestesi: currentOperationsanmalan.value?.anestesi_display })
                    : null,
                blockad: currentOperationsanmalan.value?.blockad_display
                    ? convertBlockadData({ blockad: currentOperationsanmalan.value?.blockad_display })
                    : null,
                premedicinering: currentOperationsanmalan.value?.anestesi_display
                    ? currentOperationsanmalan.value?.anestesi_display?.premedicinering_display?.map((item) => {
                          return {
                              id: convertPremedicineringData({ premedicinering: item }),
                          };
                      })
                    : [],
            };
        }

        store.dispatch("tabs/displaySpinner", { id: tabId.value, display: false });

        return formData;
    };

    const loadPreanestesibedomning = async (pk) => {
        try {
            const config = getConfig({}, "Preanestesibedömning");
            const response = await klinikenApi.get("/operation/preanestesibedomningar/" + pk + "/", config);
            let formData = cloneDeep(response.data);

            formData = {
                ...formData,
                langd: getTruncatedValue(formData?.langd),
                vikt: formData?.vikt,
                diabetes: booleanToJaNej(formData?.diabetes),
                anestesiolog: formData?.anestesiolog_display
                    ? {
                          value: formData?.anestesiolog_display?.pk,
                          label: formData?.anestesiolog_display?.user_display?.name,
                      }
                    : null,
                huvudanestesikod: convertAnestesikodData({ anestesikod: formData?.huvudanestesikod_display }),
                bianestesikoder: formData?.bianestesikoder_display?.map((item) => {
                    return {
                        id: convertAnestesikodData({ anestesikod: item }),
                    };
                }),
                anestesi: convertAnestesiData({ anestesi: formData?.anestesi_display }),
                blockad: convertBlockadData({ blockad: formData?.blockad_display }),
                premedicinering: formData?.premedicinering_display?.map((item) => {
                    return {
                        id: convertPremedicineringData({ premedicinering: item }),
                    };
                }),
                accountableHealthcareProfessional: formData?.accountableHealthcareProfessional_display
                    ? {
                          value: formData.accountableHealthcareProfessional_display.pk,
                          label: formData.accountableHealthcareProfessional_display.user_display.name,
                      }
                    : null,
            };

            isSigned.value = formData.is_signed;

            return formData;
        } catch (e) {
            console.error(e);
            openDialog("Kunde inte hämta preanestesibedömningen.", "error");
        }
    };

    const booleanToJaNej = (value) => {
        if (typeof value === "boolean") {
            return value ? "JA" : "NEJ";
        }
    };

    const saveForm = async () => {
        if (!userCanEdit.value) {
            return;
        }

        const formData = await formRef.value?.manualSubmit();

        if (!formData) {
            return;
        }

        let data = {
            ...formData,
            id: preanestesibedomningId,
            patient: patient.value.id,
            operationsanmalan: currentOperationsanmalan.value.id,
        };

        store.dispatch("tabs/displaySpinner", { id: tabId.value, display: true });

        try {
            await store.dispatch("operation/preanestesibedomning/save", data);
            await store.dispatch("operation/operationsanmalanV2/getOperationsanmalanById", {
                id: currentOperationsanmalan.value.id,
                forceRefresh: true,
                addAsUpdate: true,
            });

            flashMessage(preanestesibedomningId ? "Preanestesibedömning uppdaterad" : "Preanestesibedömning sparad");

            emitter.emit("preanestesibedomning-uppdaterad", {
                operationsanmalanId: currentOperationsanmalan.value.id,
            });

            store.dispatch("tabs/closeTab", tabId.value);
        } catch (error) {
            inputErrors.value = error.response.data;
            scrollToAndFocusError(formElement.value, nextTick);
        } finally {
            store.dispatch("tabs/displaySpinner", { id: tabId.value, display: false });
        }
    };

    const signForm = async () => {
        if (!userCanEdit.value) {
            return;
        }

        const formData = await formRef.value?.manualSubmit();

        if (!formData) {
            return;
        }

        let data = {
            ...formData,
            id: preanestesibedomningId,
            patient: patient.value.id,
            operationsanmalan: currentOperationsanmalan.value.id,
        };

        signData.value = data;
        signUrl.value = data.id
            ? `/operation/preanestesibedomningar/${data.id}/`
            : "/operation/preanestesibedomningar/";
        signVisible.value = true;
    };

    const handleSignSuccess = async (data) => {
        store.commit("operation/preanestesibedomning/setPreanestesibedomning", data);

        await store.dispatch("operation/operationsanmalanV2/getOperationsanmalanById", {
            id: currentOperationsanmalan.value.id,
            forceRefresh: true,
            addAsUpdate: true,
        });

        flashMessage("Preanestesibedömningen signerad");

        emitter.emit("preanestesibedomning-uppdaterad", {
            operationsanmalanId: currentOperationsanmalan.value.id,
        });

        store.dispatch("tabs/closeTab", tabId.value);
    };

    const handleSignError = (error) => {
        if (typeof error === "object") {
            inputErrors.value = error;
            scrollToAndFocusError(formElement.value, nextTick);
        } else {
            openDialog(error, "error");
        }

        signVisible.value = false;
    };

    const handleSignCancel = () => {
        signVisible.value = false;
    };

    const cancelForm = ({ hasUnsavedData }) => {
        if (hasUnsavedData) {
            emitter.emit("open-dialog", {
                type: "warning",
                description: "Du har osparad data. Vill du stänga utan att spara?",
                buttons: [
                    {
                        title: "Fortsätt redigera",
                        type: "secondary",
                    },
                    {
                        title: "Ja, stäng utan att spara",
                        type: "primary",
                        action: () => {
                            store.dispatch("tabs/closeTab", tabId.value);
                        },
                    },
                ],
            });
        } else {
            store.dispatch("tabs/closeTab", tabId.value);
        }
    };

    const formTriggers = [
        {
            fieldName: "hjart_karlsjukdom_text",
            dependentOn: "hjart_karlsjukdom",
            action: ({ value, dependentOnValue }) => {
                // Handle both boolean true and string "JA"
                const isHjartKarlsjukdom = dependentOnValue === true || dependentOnValue === "JA";
                return { value: isHjartKarlsjukdom ? value : null, disabled: !isHjartKarlsjukdom };
            },
        },
        {
            fieldName: "lungsjukdom_text",
            dependentOn: "lungsjukdom",
            action: ({ value, dependentOnValue }) => {
                // Handle both boolean true and string "JA"
                const isLungSjukdom = dependentOnValue === true || dependentOnValue === "JA";
                return { value: isLungSjukdom ? value : null, disabled: !isLungSjukdom };
            },
        },
        {
            fieldName: "njursjukdom_beskrivning",
            dependentOn: "njursjukdom",
            action: ({ value, dependentOnValue }) => {
                // Handle both boolean true and string "JA"
                const isNjurSjukdom = dependentOnValue === true || dependentOnValue === "JA";
                return { value: isNjurSjukdom ? value : null, disabled: !isNjurSjukdom };
            },
        },
        {
            fieldName: "blodningsbenagenhet_beskrivning",
            dependentOn: "blodningsbenagenhet",
            action: ({ value, dependentOnValue }) => {
                // Handle both boolean true and string "JA"
                const isBlodningTrombosbenagenhet = dependentOnValue === true || dependentOnValue === "JA";
                return {
                    value: isBlodningTrombosbenagenhet ? value : null,
                    disabled: !isBlodningTrombosbenagenhet,
                };
            },
        },
        {
            fieldName: "antikoagulantia_beskrivning",
            dependentOn: "antikoagulantia",
            action: ({ value, dependentOnValue }) => {
                // Handle both boolean true and string "JA"
                const isAntikoagulantia = dependentOnValue === true || dependentOnValue === "JA";
                return {
                    value: isAntikoagulantia ? value : null,
                    disabled: !isAntikoagulantia,
                };
            },
        },
        {
            fieldName: "overkanslighet_text",
            dependentOn: "overkanslighet",
            action: ({ value, dependentOnValue }) => {
                // Handle both boolean true and string "JA"
                const isOverkanslighet = dependentOnValue === true || dependentOnValue === "JA";

                return { value: isOverkanslighet ? value : null, disabled: !isOverkanslighet };
            },
        },
        {
            fieldName: "diabetes_lakemedelsform",
            dependentOn: "diabetes",
            action: ({ value, dependentOnValue }) => {
                const isDiabetes = dependentOnValue && dependentOnValue === "JA";

                return {
                    value: dependentOnValue === "JA" ? value : null,
                    disabled: !isDiabetes,
                };
            },
        },
        {
            fieldName: "blockad",
            dependentOn: "anestesi",
            action: async ({ value, dependentOnValue, selectedOption, initial }) => {
                /* dependentOnValue kan vara ett objekt vid inladdning av data i formuläret men är senare id på
                anestesi, och då tar vi första alternativet i selectedOption. */
                const isBlockad =
                    dependentOnValue?.is_blockad || (selectedOption?.length > 0 && selectedOption[0]?.is_blockad);

                return {
                    value: isBlockad || initial ? value : null,
                    disabled: !isBlockad,
                };
            },
        },
        {
            fieldName: "neurologisk_sjukdom_text",
            dependentOn: "neurologisk_sjukdom",
            action: ({ value, dependentOnValue }) => {
                return { value: dependentOnValue ? value : null, disabled: !dependentOnValue };
            },
        },
        {
            fieldName: ["bianestesikoder", "huvudanestesikod"],
            dependentOn: "anestesi",
            action: async ({ value, dependentOnValue, selectedOption, initial }) => {
                if (initial) {
                    return;
                }

                if (dependentOnValue) {
                    let storeAnestesi = store.getters["operation/anestesi/anestesiById"]({
                        id: dependentOnValue,
                    });

                    // Konvertera huvudanestesikod
                    const huvudanestesikodValue = storeAnestesi.huvudanestesikod_display
                        ? convertDiagnoskodData({ diagnoskod: storeAnestesi.huvudanestesikod_display })
                        : null;

                    // Konvertera bianestesikoder
                    const bianestesikoderValue = Array.isArray(storeAnestesi.bianestesikoder_display)
                        ? storeAnestesi.bianestesikoder_display.map((item) => ({
                              id: convertDiagnoskodData({ diagnoskod: item }),
                          }))
                        : [];

                    return [
                        {
                            fieldName: "huvudanestesikod",
                            value: huvudanestesikodValue,
                            disabled: false,
                            required: false,
                        },
                        {
                            fieldName: "bianestesikoder",
                            value: bianestesikoderValue,
                            disabled: false,
                            required: false,
                        },
                    ];
                }
            },
        },
        {
            fieldName: "datum_godkannande",
            dependentOn: "godkannande",
            action: ({ value, dependentOnValue }) => {
                const nowDate = dependentOnValue === godkand && !value ? moment().format("YYYY-MM-DD") : null;

                return nowDate ? { value: nowDate, disabled: false } : { value, disabled: true };
            },
        },
        {
            fieldName: "giltighetstid",
            dependentOn: "godkannande",
            action: ({ value, dependentOnValue }) => {
                const sixMonthsLater =
                    dependentOnValue === godkand && !value ? moment().add(6, "months").format("YYYY-MM-DD") : null;

                return sixMonthsLater ? { value: sixMonthsLater, disabled: false } : { value, disabled: true };
            },
        },
        {
            fieldName: "komplettering_kravd_datum",
            dependentOn: "godkannande",
            action: ({ value, dependentOnValue }) => {
                const isKompletteringKravd = dependentOnValue === kompletteringKravs;

                return {
                    value: isKompletteringKravd && !value ? moment().format("YYYY-MM-DD") : value,
                    disabled: !isKompletteringKravd,
                    required:
                        formRef.value.getFieldValue({ name: "godkannande" }) === kompletteringKravs ? "required" : "",
                };
            },
        },
        {
            fieldName: "komplettering_utford",
            dependentOn: "godkannande",
            action: ({ value, dependentOnValue, initial }) => {
                if (initial) {
                    return {
                        value: value,
                    };
                }

                return {
                    value: !dependentOnValue || dependentOnValue === kompletteringKravs ? false : value,
                };
            },
        },
    ];

    const formListeners = () => [
        ...(bmiDisplay?.value?.formListeners ?? []),
        {
            fieldNames: ["godkannande"],
            action: ({ values }) => {
                godkannande.value = values.godkannande;
            },
        },
        {
            fieldNames: ["accountableHealthcareProfessional"],
            runAtInitial: true,
            action: ({ values }) => {
                accountableHealthcareProfessional.value =
                    values.accountableHealthcareProfessional?.value ?? values.accountableHealthcareProfessional;
            },
        },
    ];

    const formConverters = [
        {
            fieldName: "hjart_karlsjukdom",
            converter: ({ value }) => {
                if (typeof value === "string") {
                    return value === "JA";
                }

                return value;
            },
        },
        {
            fieldName: "lungsjukdom",
            converter: ({ value }) => {
                if (typeof value === "string") {
                    return value === "JA";
                }

                return value;
            },
        },
        {
            fieldName: "njursjukdom",
            converter: ({ value }) => {
                if (typeof value === "string") {
                    return value === "JA";
                }

                return value;
            },
        },
        {
            fieldName: "blodningsbenagenhet",
            converter: ({ value }) => {
                if (typeof value === "string") {
                    return value === "JA";
                }

                return value;
            },
        },
        {
            fieldName: "antikoagulantia",
            converter: ({ value }) => {
                if (typeof value === "string") {
                    return value === "JA";
                }

                return value;
            },
        },
        {
            fieldName: "overkanslighet",
            converter: ({ value }) => {
                if (typeof value === "string") {
                    return value === "JA";
                }

                return value;
            },
        },
        {
            fieldName: "diabetes",
            converter: ({ value }) => {
                if (typeof value === "string") {
                    return value === "JA";
                }

                return null;
            },
        },
    ];

    const openOperationsinformation = async () => {
        await store.dispatch("tabs/openTab", {
            component: Operationsinformation,
            parameters: { operationsanmalan_id: currentOperationsanmalan.value.id },
        });
    };
</script>
